import { useCallback, useEffect, useMemo, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, Download, Gauge, MapPin, XCircle } from 'lucide-react';

const statusStyles = {
  pending: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-red-50 text-red-600',
};

export default function MileagePage() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchMileage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = statusFilter === 'all' ? {} : { status: statusFilter };
      const [logsResp, summaryResp] = await Promise.all([
        api.get('/mileage', { params }),
        api.get('/mileage/summary', { params }),
      ]);
      setLogs(logsResp.data);
      setSummary(summaryResp.data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchMileage(); }, [fetchMileage]);

  const totals = useMemo(() => logs.reduce((acc, log) => acc + (Number(log.total_miles) || 0), 0), [logs]);

  const reviewMileage = async (mileageId, status) => {
    setBusyId(mileageId);
    setError('');
    try {
      await api.put(`/mileage/${mileageId}/review`, { status, review_note: notes[mileageId] || '' });
      await fetchMileage();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId('');
    }
  };

  const exportCsv = async () => {
    setError('');
    try {
      const params = statusFilter === 'all' ? {} : { status: statusFilter };
      const response = await api.get('/mileage/export.csv', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'fieldcrm-mileage.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="mileage-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950 font-heading" data-testid="mileage-title">Mileage Tracker</h1>
          <p className="mt-1 text-sm text-zinc-500" data-testid="mileage-summary-text">{logs.length} logs · {totals.toFixed(1)} miles in this queue</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={exportCsv} data-testid="mileage-export-button">
            <Download className="mr-1 h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="mileage-kpis">
        <Card className="border border-zinc-200"><CardContent className="p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Total Miles</p><p className="text-2xl font-bold text-zinc-950" data-testid="mileage-kpi-total-miles">{summary?.total_miles?.toFixed ? summary.total_miles.toFixed(1) : Number(summary?.total_miles || 0).toFixed(1)}</p></CardContent></Card>
        {(summary?.by_status || ['pending', 'approved', 'rejected'].map(status => ({ status, miles: 0, count: 0 }))).slice(0, 3).map(row => (
          <Card key={row.status} className="border border-zinc-200" data-testid={`mileage-kpi-${row.status}`}><CardContent className="p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{row.status}</p><p className="text-2xl font-bold text-zinc-950">{Number(row.miles || 0).toFixed(1)}</p><p className="text-xs text-zinc-400">{row.count || 0} logs</p></CardContent></Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="mileage-status-filters">
        {['pending', 'approved', 'rejected', 'all'].map(status => (
          <Button key={status} type="button" variant={statusFilter === status ? 'default' : 'outline'} size="sm" onClick={() => setStatusFilter(status)} data-testid={`mileage-filter-${status}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Button>
        ))}
      </div>

      {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="mileage-error">{error}</div>}

      {loading ? (
        <div className="space-y-3" data-testid="mileage-loading">{[1, 2, 3].map(item => <div key={item} className="h-36 rounded-md bg-zinc-200 animate-pulse" />)}</div>
      ) : logs.length === 0 ? (
        <Card className="border border-zinc-200" data-testid="mileage-empty"><CardContent className="py-12 text-center"><Gauge className="mx-auto mb-2 h-8 w-8 text-zinc-400" /><p className="text-sm text-zinc-500">No mileage logs in this queue.</p></CardContent></Card>
      ) : (
        <div className="grid gap-3" data-testid="mileage-list">
          {logs.map(log => {
            const isComplete = !(log.mileage_complete === false || log.odometer_end == null);
            return (
            <Card key={log.id} className="border border-zinc-200" data-testid={`mileage-card-${log.id}`}>
              <CardHeader className="pb-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base font-bold font-heading">
                      <Gauge className="h-4 w-4" />
                      <span data-testid={`mileage-driver-${log.id}`}>{log.driver_name || 'Unknown driver'}</span>
                      <span data-testid={`mileage-miles-${log.id}`}>{isComplete ? `${Number(log.total_miles || 0).toFixed(1)} mi` : 'Start saved'}</span>
                    </CardTitle>
                    <p className="mt-1 text-xs text-zinc-500" data-testid={`mileage-date-${log.id}`}>{log.date} · {log.vehicle_label || 'No vehicle'} · {log.job_title || 'General business trip'}</p>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {!isComplete && <Badge variant="secondary" className="w-fit bg-sky-50 text-xs text-sky-700" data-testid={`mileage-incomplete-${log.id}`}>Needs ending mileage</Badge>}
                    <Badge variant="secondary" className={`w-fit text-xs ${statusStyles[log.status] || 'bg-zinc-100 text-zinc-600'}`} data-testid={`mileage-status-${log.id}`}>{log.status}</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-zinc-800" data-testid={`mileage-purpose-${log.id}`}>{log.business_purpose}</p>
                <div className="grid gap-2 text-xs text-zinc-500 sm:grid-cols-2" data-testid={`mileage-locations-${log.id}`}>
                  <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> From: {log.start_location || '—'}</span>
                  <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> To: {log.end_location || '—'}</span>
                  <span>Odometer: {log.odometer_start} → {log.odometer_end ?? 'pending'}</span>
                  <span>Created: {log.created_at ? new Date(log.created_at).toLocaleString() : '—'}</span>
                </div>
                <Textarea value={notes[log.id] ?? log.review_note ?? ''} onChange={(event) => setNotes(prev => ({ ...prev, [log.id]: event.target.value }))} placeholder="Add review notes..." rows={2} data-testid={`mileage-review-note-${log.id}`} />
                <div className="flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" disabled={busyId === log.id} onClick={() => reviewMileage(log.id, 'rejected')} data-testid={`mileage-review-reject-${log.id}`}><XCircle className="mr-1 h-4 w-4" /> Reject</Button>
                  <Button type="button" disabled={busyId === log.id || !isComplete} onClick={() => reviewMileage(log.id, 'approved')} data-testid={`mileage-review-approve-${log.id}`}><CheckCircle className="mr-1 h-4 w-4" /> Approve</Button>
                </div>
              </CardContent>
            </Card>
          );})}
        </div>
      )}
    </div>
  );
}